import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, useToast } from '@caruso/ui';
import { venuesService } from '@/services/venues.service';
import { uploadService } from '@/services/upload.service';
import { aiService } from '@/services/ai.service';
import { ArrowLeft, Save, Plus, X, Upload, Star, Sparkles } from 'lucide-react';
import { CreateVenueDto, PricingType } from '@caruso/types';

export function VenueFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEdit = !!id;

  const [images, setImages] = useState<Array<{ url: string; isPrimary: boolean; order: number }>>([]);
  const [customPrices, setCustomPrices] = useState<Array<{ date: string; price: number }>>([]);
  const [uploading, setUploading] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [availableDays, setAvailableDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [priceFormatted, setPriceFormatted] = useState('');

  // Format currency input
  const formatCurrency = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    const number = parseInt(digits) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const parseCurrency = (value: string): number => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return 0;
    return parseInt(digits) / 100;
  };

  const handlePriceChange = (value: string) => {
    const formatted = formatCurrency(value);
    setPriceFormatted(formatted);
  };

  const daysOfWeek = [
    { value: 0, label: 'Dom' },
    { value: 1, label: 'Seg' },
    { value: 2, label: 'Ter' },
    { value: 3, label: 'Qua' },
    { value: 4, label: 'Qui' },
    { value: 5, label: 'Sex' },
    { value: 6, label: 'Sáb' },
  ];

  const { register, handleSubmit, setValue, watch } = useForm<CreateVenueDto>({
    defaultValues: {
      pricingType: PricingType.DAILY,
      active: true,
      featured: false,
      showPricePublicly: true,
      openTime: '08:00',
      closeTime: '22:00',
    },
  });

  const { data: venue } = useQuery({
    queryKey: ['venue', id],
    queryFn: () => venuesService.getById(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (venue) {
      setValue('name', venue.name);
      setValue('description', venue.description || '');
      setValue('capacity', venue.capacity);
      setValue('area', venue.area);
      setValue('pricingType', venue.pricingType);
      setValue('showPricePublicly', venue.showPricePublicly ?? true);
      setValue('openTime', venue.openTime || '08:00');
      setValue('closeTime', venue.closeTime || '22:00');
      setValue('active', venue.active);
      setValue('featured', venue.featured);
      setAvailableDays(venue.availableDays || [0, 1, 2, 3, 4, 5, 6]);
      setImages(venue.images || []);
      setCustomPrices(venue.customPrices?.map(cp => ({ date: new Date(cp.date).toISOString().split('T')[0], price: cp.price })) || []);
      // Format price when loading
      if (venue.defaultPrice) {
        setPriceFormatted(venue.defaultPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      }
    }
  }, [venue, setValue]);

  const mutation = useMutation({
    mutationFn: async (data: CreateVenueDto) => {
      const payload = {
        ...data,
        defaultPrice: parseCurrency(priceFormatted),
        images,
        customPrices,
        availableDays
      };
      return isEdit ? venuesService.update(id!, payload) : venuesService.create(payload);
    },
    onSuccess: () => {
      toast({ title: `Ambiente ${isEdit ? 'atualizado' : 'criado'}!`, variant: 'success' });
      navigate('/app/venues');
    },
    onError: () => {
      toast({ title: 'Erro ao salvar ambiente', variant: 'destructive' });
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (images.length + files.length > 10) {
      toast({ title: 'Máximo de 10 fotos', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      for (const file of files) {
        const result = await uploadService.uploadFile(file, 'venues');
        setImages(prev => [...prev, { url: result.url, isPrimary: prev.length === 0, order: prev.length }]);
      }
      toast({ title: 'Fotos enviadas!', variant: 'success' });
    } catch (error) {
      toast({ title: 'Erro ao enviar fotos', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateDescription = async () => {
    const name = watch('name');
    const area = watch('area');
    const capacity = watch('capacity');
    const currentDescription = watch('description');

    if (!name) {
      toast({ title: 'Preencha o nome do ambiente primeiro', variant: 'destructive' });
      return;
    }

    setGeneratingDescription(true);
    try {
      const result = await aiService.generateVenueDescription({
        name,
        area,
        capacity,
        currentDescription,
        images: images.map(img => img.url),
      });

      setValue('description', result.description);
      toast({ title: 'Descrição gerada com sucesso!', variant: 'success' });
    } catch (error: any) {
      console.error('Error generating description:', error);
      toast({
        title: 'Erro ao gerar descrição',
        description: error.response?.data?.message || error.message || 'Tente novamente',
        variant: 'destructive'
      });
    } finally {
      setGeneratingDescription(false);
    }
  };

  const onSubmit = (data: CreateVenueDto) => {
    mutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/app/venues')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-3xl font-bold text-brand-gold">
          {isEdit ? 'Editar Ambiente' : 'Novo Ambiente'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">Nome do Ambiente *</label>
              <Input {...register('name', { required: true })} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-brand-ivory">Descrição</label>
                <Button
                  type="button"
                  onClick={handleGenerateDescription}
                  disabled={generatingDescription || !watch('name')}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {generatingDescription ? 'Gerando...' : 'Gerar com IA'}
                </Button>
              </div>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full p-2 bg-brand-ink border border-brand-gold/20 rounded text-brand-ivory"
                placeholder="Descreva o ambiente ou clique em 'Gerar com IA' para criar uma descrição automática"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Capacidade (pessoas)</label>
                <Input type="number" {...register('capacity', { valueAsNumber: true })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Área (m²)</label>
                <Input type="number" step="0.01" {...register('area', { valueAsNumber: true })} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Precificação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">Tipo de Cobrança *</label>
              <select {...register('pricingType')} className="w-full p-2 bg-brand-ink border border-brand-gold/20 rounded text-brand-ivory">
                <option value="daily">Diária</option>
                <option value="hourly">Por Hora</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">Preço Padrão *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ivory/60">R$</span>
                <Input
                  value={priceFormatted}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  placeholder="0,00"
                  className="pl-10"
                />
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input type="checkbox" {...register('showPricePublicly')} className="w-4 h-4 accent-brand-gold" />
              <span className="text-brand-ivory group-hover:text-brand-gold transition-colors">Mostrar preço na página pública</span>
            </label>
          </CardContent>
        </Card>

        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Configuração de Disponibilidade</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">Dias Disponíveis para Locação</label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map((day) => (
                  <label
                    key={day.value}
                    className={`cursor-pointer px-4 py-2 rounded border transition-all ${
                      availableDays.includes(day.value)
                        ? 'bg-brand-gold text-brand-ink border-brand-gold'
                        : 'bg-brand-ink border-brand-gold/20 text-brand-ivory hover:border-brand-gold/40'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={availableDays.includes(day.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAvailableDays([...availableDays, day.value].sort());
                        } else {
                          setAvailableDays(availableDays.filter((d) => d !== day.value));
                        }
                      }}
                    />
                    {day.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Horário de Abertura</label>
                <Input type="time" {...register('openTime')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-ivory mb-2">Horário de Fechamento</label>
                <Input type="time" {...register('closeTime')} />
              </div>
            </div>
            <p className="text-sm text-brand-ivory/60">
              Os agendamentos só poderão ser feitos dentro do horário configurado e nos dias selecionados.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Fotos (máx. 10)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {images.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img src={img.url} className="w-full h-32 object-cover rounded border border-brand-gold/20" />
                  {img.isPrimary && <Star className="absolute top-2 left-2 w-5 h-5 text-brand-gold fill-brand-gold" />}
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
                    onClick={() => setImages(images.filter((_, i) => i !== idx))}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  {!img.isPrimary && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100"
                      onClick={() => setImages(images.map((im, i) => ({ ...im, isPrimary: i === idx })))}
                    >
                      Marcar como principal
                    </Button>
                  )}
                </div>
              ))}
              {images.length < 10 && (
                <label className="w-full h-32 border-2 border-dashed border-brand-gold/20 rounded flex items-center justify-center cursor-pointer hover:border-brand-gold/40">
                  <input type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                  {uploading ? 'Enviando...' : <Upload className="w-6 h-6 text-brand-gold/50" />}
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <CardHeader>
            <CardTitle className="text-brand-gold">Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input type="checkbox" {...register('active')} className="w-4 h-4 accent-brand-gold" />
              <span className="text-brand-ivory group-hover:text-brand-gold transition-colors">Ambiente ativo</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input type="checkbox" {...register('featured')} className="w-4 h-4 accent-brand-gold" />
              <span className="text-brand-ivory group-hover:text-brand-gold transition-colors">Destacar na listagem</span>
            </label>
          </CardContent>
        </Card>

        <div className="flex gap-4 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/app/venues')}>
            Cancelar
          </Button>
          <Button type="submit" variant="premium" disabled={mutation.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {mutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
